// Platform announcement emails to shop owners — ONE renderer shared by the
// admin preview (src, via Vite) and the announce-email edge function (Deno),
// so what the admin previews is byte-for-byte what owners receive.
//
// Plain JS, no imports, no Deno/DOM APIs. Email-client safe: table layout,
// inline styles only, solid bgcolor fallbacks under every gradient (Outlook
// ignores CSS gradients), a bulletproof button, ≤600px wide.

export const ANNOUNCE_TEMPLATES = {
  feature: {
    label: "🚀 最新功能更新",
    kicker: "NEW FEATURE · 功能更新",
    color: "#7C3AED", color2: "#EC4899", tint: "#F5F3FF",
    sample: {
      subject: "PettoBook 新功能：客戶可以直接喺網上同你傾偈",
      preheader: "訊息直達你嘅收件箱，AI 仲可以幫你即時回覆。",
      headline: "客戶訊息，一個收件箱搞掂",
      body: "我哋剛推出咗幾個新功能，等你少啲覆訊息、多啲時間做美容。\n\n全部已經自動開通，登入就用得。",
      items: [
        { icon: "💬", title: "客戶網上訊息", desc: "客戶喺預約頁直接搵你，訊息同 WhatsApp 一齊出現喺「訊息」。" },
        { icon: "🤖", title: "AI 自動回覆", desc: "問價、問時間、改期，AI 按你嘅價目同營業時間即時回答。" },
        { icon: "📸", title: "相冊放大檢視", desc: "客戶睇 Before / After 相可以全螢幕左右掃。" },
      ],
      ctaLabel: "立即試用",
      ctaUrl: "https://pettobook.com",
    },
  },
  promotion: {
    label: "🎁 優惠推廣",
    kicker: "LIMITED OFFER · 限時優惠",
    color: "#E11D48", color2: "#F97316", tint: "#FFF1F2",
    sample: {
      subject: "限時優惠：升級專業版首 3 個月 8 折",
      preheader: "本月內升級即享優惠，名額有限。",
      headline: "升級專業版，首 3 個月 8 折",
      body: "多謝你一直使用 PettoBook！今個月升級到專業版，即享首 3 個月 8 折。\n\n專業版包括電子積分卡、進階排班、AI 助手等功能，幫你留住熟客。",
      offer: "8 折",
      offerNote: "首 3 個月",
      code: "GROOM20",
      expiry: "優惠有效期至 2026 年 10 月 31 日",
      ctaLabel: "立即升級",
      ctaUrl: "https://pettobook.com",
    },
  },
  notice: {
    label: "🛠️ 系統通知",
    kicker: "SERVICE NOTICE · 系統通知",
    color: "#2563EB", color2: "#0EA5E9", tint: "#EFF6FF",
    sample: {
      subject: "系統維護通知：10 月 20 日凌晨",
      preheader: "維護期間預約頁會暫停約 30 分鐘。",
      headline: "系統維護通知",
      body: "為咗提升速度同穩定性，我哋會進行系統維護。\n\n維護期間客戶暫時未能網上預約，已有預約不受影響。完成後毋須任何操作。",
      when: "2026 年 10 月 20 日（星期二）凌晨 2:00 – 2:30",
      ctaLabel: "",
      ctaUrl: "",
    },
  },
  general: {
    label: "📰 一般消息",
    kicker: "PETTOBOOK NEWS · 最新消息",
    color: "#DB2777", color2: "#FB7185", tint: "#FDF2F8",
    sample: {
      subject: "PettoBook 最新消息",
      preheader: "",
      headline: "多謝你選擇 PettoBook",
      body: "想同你分享平台嘅最新消息。\n\n有任何意見，歡迎直接回覆呢封電郵。",
      ctaLabel: "開啟 PettoBook",
      ctaUrl: "https://pettobook.com",
    },
  },
};

const LOGO = "https://pettobook.com/icon-192.png";
const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'PingFang HK','Noto Sans TC','Microsoft JhengHei',Helvetica,Arial,sans-serif";

function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function safeUrl(u) {
  const s = String(u || "").trim();
  return /^https?:\/\//i.test(s) ? s : "";
}
function paras(text, style) {
  return String(text || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p style="${style}">${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
}

// e: { template, subject, preheader, headline, body, ctaLabel, ctaUrl,
//      items[{icon,title,desc}], offer, offerNote, code, expiry, when }
// opts: { name, shopName, unsubscribeUrl }
export function renderAnnouncementEmail(e, opts = {}) {
  const tpl = ANNOUNCE_TEMPLATES[e.template] || ANNOUNCE_TEMPLATES.general;
  const c = tpl.color, c2 = tpl.color2;
  const greet = opts.shopName ? `${opts.shopName} 你好，` : opts.name ? `${opts.name} 你好，` : "你好，";
  const P = `margin:0 0 16px;font-family:${FONT};font-size:16px;line-height:1.7;color:#3F3D56;`;
  const cta = safeUrl(e.ctaUrl) && e.ctaLabel ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 8px"><tr>
      <td bgcolor="${c}" style="border-radius:999px;background:${c};background-image:linear-gradient(135deg,${c},${c2});">
        <a href="${esc(safeUrl(e.ctaUrl))}" target="_blank" style="display:inline-block;padding:15px 34px;font-family:${FONT};font-size:16px;font-weight:800;color:#ffffff;text-decoration:none;border-radius:999px;">${esc(e.ctaLabel)} →</a>
      </td></tr></table>` : "";

  let block = "";
  const items = (e.items || []).filter((x) => x && (x.title || x.desc));
  if (e.template === "feature" && items.length) {
    block = items.map((x) => `
      <tr><td style="padding:0 0 12px">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${tpl.tint};border-radius:14px"><tr>
          <td width="56" valign="top" style="padding:16px 0 16px 16px;font-size:26px;line-height:1">${esc(x.icon || "✨")}</td>
          <td valign="top" style="padding:16px 16px 16px 6px;font-family:${FONT}">
            <div style="font-size:16px;font-weight:800;color:#1F1B2E;margin:0 0 4px">${esc(x.title)}</div>
            <div style="font-size:14px;line-height:1.6;color:#5B5873">${esc(x.desc)}</div>
          </td></tr></table>
      </td></tr>`).join("");
    block = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 12px">${block}</table>`;
  } else if (e.template === "promotion" && (e.offer || e.code)) {
    block = `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 20px"><tr>
        <td align="center" style="background:${tpl.tint};border:2px dashed ${c};border-radius:18px;padding:24px 16px;font-family:${FONT}">
          ${e.offer ? `<div style="font-size:44px;font-weight:900;color:${c};line-height:1.1">${esc(e.offer)}</div>` : ""}
          ${e.offerNote ? `<div style="font-size:14px;font-weight:700;color:#5B5873;margin-top:4px">${esc(e.offerNote)}</div>` : ""}
          ${e.code ? `<div style="margin-top:16px;font-size:12px;font-weight:700;color:#8A87A0;letter-spacing:1px">優惠碼 · PROMO CODE</div>
            <div style="display:inline-block;margin-top:6px;padding:10px 22px;background:#ffffff;border-radius:10px;font-family:Menlo,Consolas,monospace;font-size:22px;font-weight:800;letter-spacing:3px;color:#1F1B2E">${esc(e.code)}</div>` : ""}
          ${e.expiry ? `<div style="margin-top:14px;font-size:13px;font-weight:700;color:${c}">⏰ ${esc(e.expiry)}</div>` : ""}
        </td></tr></table>`;
  } else if (e.template === "notice" && e.when) {
    block = `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 20px"><tr>
        <td style="background:${tpl.tint};border-left:4px solid ${c};border-radius:10px;padding:16px 18px;font-family:${FONT}">
          <div style="font-size:12px;font-weight:800;color:${c};letter-spacing:1px;margin-bottom:4px">🗓 時間 · WHEN</div>
          <div style="font-size:16px;font-weight:800;color:#1F1B2E;line-height:1.5">${esc(e.when)}</div>
        </td></tr></table>`;
  }

  const unsub = safeUrl(opts.unsubscribeUrl);
  const html = `<!doctype html>
<html lang="zh-HK"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light only"><title>${esc(e.subject)}</title></head>
<body style="margin:0;padding:0;background:#F4F2F8;-webkit-text-size-adjust:100%">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${esc(e.preheader || "")}&#8204;&nbsp;&#8204;&nbsp;&#8204;&nbsp;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#F4F2F8" style="background:#F4F2F8"><tr><td align="center" style="padding:28px 12px">
  <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px">
    <tr><td style="padding:0 6px 16px">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
        <td valign="middle"><img src="${LOGO}" width="36" height="36" alt="PettoBook" style="display:block;border-radius:10px;border:0"></td>
        <td valign="middle" style="padding-left:10px;font-family:${FONT};font-size:18px;font-weight:900;color:#1F1B2E">PettoBook</td>
      </tr></table>
    </td></tr>
    <tr><td bgcolor="${c}" style="background:${c};background-image:linear-gradient(135deg,${c} 0%,${c2} 100%);border-radius:22px 22px 0 0;padding:36px 36px 32px">
      <div style="display:inline-block;padding:6px 12px;border-radius:999px;background:rgba(255,255,255,0.22);font-family:${FONT};font-size:11px;font-weight:800;letter-spacing:1.2px;color:#ffffff">${esc(tpl.kicker)}</div>
      <h1 style="margin:16px 0 0;font-family:${FONT};font-size:28px;line-height:1.3;font-weight:900;color:#ffffff">${esc(e.headline || e.subject)}</h1>
    </td></tr>
    <tr><td bgcolor="#ffffff" style="background:#ffffff;border-radius:0 0 22px 22px;padding:32px 36px 36px">
      <p style="${P}font-weight:700;color:#1F1B2E">${esc(greet)}</p>
      ${paras(e.body, P)}
      ${block}
      ${cta}
      <p style="margin:28px 0 0;font-family:${FONT};font-size:14px;line-height:1.6;color:#8A87A0">有問題？直接回覆呢封電郵，我哋會盡快覆你。<br>PettoBook 團隊 🐾</p>
    </td></tr>
    <tr><td align="center" style="padding:22px 20px 8px;font-family:${FONT};font-size:12px;line-height:1.7;color:#9A97B0">
      你收到此電郵，是因為你喺 PettoBook 開設咗店舖帳戶。<br>
      <a href="https://pettobook.com" style="color:#9A97B0;text-decoration:underline">pettobook.com</a>${unsub ? ` &nbsp;·&nbsp; <a href="${esc(unsub)}" style="color:#9A97B0;text-decoration:underline">取消訂閱 Unsubscribe</a>` : ""}
    </td></tr>
  </table>
</td></tr></table>
</body></html>`;

  const text = [
    greet, "", e.headline || "", "", String(e.body || ""),
    ...items.map((x) => `• ${x.title}${x.desc ? " — " + x.desc : ""}`),
    e.offer ? `${e.offer} ${e.offerNote || ""}` : "", e.code ? `優惠碼：${e.code}` : "", e.expiry || "", e.when ? `時間：${e.when}` : "",
    safeUrl(e.ctaUrl) && e.ctaLabel ? `${e.ctaLabel}：${safeUrl(e.ctaUrl)}` : "",
    "", "PettoBook 團隊", unsub ? `取消訂閱：${unsub}` : "",
  ].filter((x, i, a) => x !== "" || a[i - 1] !== "").join("\n");

  return { subject: String(e.subject || e.headline || "PettoBook"), html, text };
}
